import { Check, LockSimple, Upload } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

export function OfferExportDialog({ open, onClose, rows, selectedSetNumbers }) {
  const [working, setWorking] = useState([]);
  const [includeBl, setIncludeBl] = useState(false);
  const [useSmart, setUseSmart] = useState(true);
  const [approvedOnly, setApprovedOnly] = useState(false);

  useEffect(() => {
    if (!open) return;
    const pool = selectedSetNumbers
      ? rows.filter(r => selectedSetNumbers.includes(r.set_number))
      : rows;
    setWorking(
      pool
        .filter(r => (r.suggested_offer && r.suggested_offer.available) || r.offer_approved || r.smart_offer?.available)
        .filter(r => !approvedOnly || r.offer_approved)
        .map(r => {
          const so = r.suggested_offer || {};
          const isMaxBuy = so.mode === "max_buy";
          const approved = !!r.offer_approved && r.approved_offer != null;
          const smart = r.smart_offer?.available ? r.smart_offer.recommended : null;
          // Priority: approved (locked) > your manual adjustment > Smart Offer (if switched on) > auto
          //   auto max-buy mode: max_buy_at_target_roi (we haven't bought yet)
          //   auto offer mode:   buy_cost (already agreed) or recommended fallback
          const auto = isMaxBuy ? so.max_buy_at_target_roi : (r.buy_cost != null ? r.buy_cost : so.recommended);
          const offer = approved ? r.approved_offer
            : r.offer_is_manual ? r.effective_offer
            : (useSmart && smart != null) ? smart
            : (auto ?? smart);
          const feasible = approved || (isMaxBuy ? so.feasible : so.profitable) || (useSmart && smart != null);
          return {
            id: r.id,
            set_number: r.set_number,
            name: r.name || "",
            mode: so.mode || "offer",
            approved,
            smart,
            offer,
            saved_offer: offer,
            bl: (r.last_scan_bricklink || {}).lowest_sealed_us,
            bl_manual: (r.last_scan_bricklink || {}).manual || false,
            include: feasible,
          };
        })
    );
  }, [open, rows, selectedSetNumbers, useSmart, approvedOnly]);

  const setField = (idx, k, v) =>
    setWorking(w => w.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
  // Persist an edited offer onto the deal so the Total Offer KPI + table agree.
  const persistOffer = async (idx) => {
    const r = working[idx];
    const n = Number(r?.offer);
    if (!r?.id || !Number.isFinite(n) || n <= 0 || n === Number(r.saved_offer)) return;
    try {
      await api.setDealOffer(r.id, n);
      setWorking(w => w.map((x, i) => (i === idx ? { ...x, saved_offer: n } : x)));
    } catch { toast.error("Couldn't save adjusted offer"); }
  };

  const includedRows = working.filter(w => w.include && Number(w.offer) > 0);
  const includedCount = includedRows.length;

  const setAllIncluded = (v) => setWorking(w => w.map(r => ({ ...r, include: v })));

  const csvEscape = (s) => {
    const v = String(s ?? "");
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };

  const exportCsv = () => {
    if (!includedCount) { toast.error("Tick at least one row to export"); return; }
    const header = ["Set Number", "Title", "Offer"];
    if (includeBl) header.push("BrickLink Sealed");
    const lines = [header.map(csvEscape).join(",")];
    for (const r of includedRows) {
      const price = `$${Number(r.offer).toFixed(2)}`;
      const line = [r.set_number, r.name, price];
      if (includeBl) line.push(r.bl != null ? `$${Number(r.bl).toFixed(2)}` : "");
      lines.push(line.map(csvEscape).join(","));
    }
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `offers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${includedCount} offer(s)`);
  };

  const copyClipboard = async () => {
    if (!includedCount) { toast.error("Tick at least one row to copy"); return; }
    const header = ["Set Number", "Title", "Offer"];
    if (includeBl) header.push("BrickLink Sealed");
    const lines = [header.join("\t")];
    for (const r of includedRows) {
      const price = `$${Number(r.offer).toFixed(2)}`;
      const line = [r.set_number, r.name, price];
      if (includeBl) line.push(r.bl != null ? `$${Number(r.bl).toFixed(2)}` : "");
      lines.push(line.join("\t"));
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success(`Copied ${includedCount} row(s) — paste into Google Sheets`);
    } catch { toast.error("Copy blocked by browser"); }
  };

  const allChecked = working.length > 0 && working.every(r => r.include);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E4E0] rounded-sm text-gray-900 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-head uppercase tracking-tighter">
            Make Offers · Client Sheet {selectedSetNumbers ? "(SELECTED)" : "(ALL DEALS)"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] font-mono text-gray-500 leading-relaxed">
          Sheet the seller sees — set number, title, and your offer only.
          Amazon prices and ASINs stay private.
        </div>
        <div className="flex flex-wrap items-center gap-4 text-[11px] font-mono border-y border-[#E4E4E0] py-2" data-testid="offer-summary">
          <span><span className="text-gray-500">Included:</span> <span className="text-[#00A850]">{includedCount}</span> / {working.length}</span>
          <span><span className="text-gray-500">Total:</span> <span className="text-[#E3000B]" data-testid="offer-sheet-total">${includedRows.reduce((a, r) => a + Number(r.offer), 0).toFixed(2)}</span></span>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none ml-auto" data-testid="offer-smart-toggle"
            title="Use the SMART OFFER (% of PriceCharting, ROI-checked) instead of Max Buy for rows you haven't adjusted or approved">
            <input type="checkbox" checked={useSmart} onChange={(e) => setUseSmart(e.target.checked)} className="accent-[#E3000B] w-3.5 h-3.5 cursor-pointer" />
            <span className="text-gray-600 uppercase text-[10px] tracking-wider">Use Smart Offers</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="offer-approved-toggle"
            title="Only rows you've approved (locked) on the Deals table">
            <input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} className="accent-[#00A850] w-3.5 h-3.5 cursor-pointer" />
            <span className="text-gray-600 uppercase text-[10px] tracking-wider">Approved only</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="offer-bl-toggle">
            <input
              type="checkbox"
              checked={includeBl}
              onChange={(e) => setIncludeBl(e.target.checked)}
              className="accent-[#0055BF] w-3.5 h-3.5 cursor-pointer"
            />
            <span className="text-gray-600 uppercase text-[10px] tracking-wider">
              Include BrickLink reference
            </span>
          </label>
        </div>
        <div className="overflow-y-auto flex-1 border border-[#E4E4E0] rounded-sm">
          <table className="w-full text-xs font-mono">
            <thead className="bg-[#F6F6F3] border-b border-[#E4E4E0] sticky top-0">
              <tr>
                <th className="px-2 py-2 w-6 text-center">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => setAllIncluded(e.target.checked)}
                    className="accent-[#00A850] w-3 h-3 cursor-pointer"
                    title={allChecked ? "Uncheck all" : "Check all"}
                    data-testid="offer-check-all"
                  />
                </th>
                <th className="px-3 py-2 text-left text-gray-500 uppercase text-[10px]">Set #</th>
                <th className="px-3 py-2 text-left text-gray-500 uppercase text-[10px]">Title</th>
                <th className="px-3 py-2 text-right text-gray-500 uppercase text-[10px]">Offer ($)</th>
                {includeBl && (
                  <th className="px-3 py-2 text-right text-gray-500 uppercase text-[10px]">BL Sealed</th>
                )}
              </tr>
            </thead>
            <tbody>
              {working.length === 0 && (
                <tr><td colSpan={includeBl ? 5 : 4} className="p-6 text-center text-gray-500">
                  Nothing to preview — no deals with a generated offer yet.
                </td></tr>
              )}
              {working.map((r, idx) => (
                <tr key={r.id} className="border-b border-[#F6F6F3] hover:bg-[#F6F6F3]">
                  <td className="px-2 py-1.5 text-center">
                    <input
                      type="checkbox"
                      checked={r.include}
                      onChange={() => setField(idx, "include", !r.include)}
                      className="accent-[#00A850] w-3 h-3 cursor-pointer"
                      data-testid={`offer-include-${r.set_number}`}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-[#E3000B] whitespace-nowrap">{r.set_number}{r.approved && <LockSimple size={9} weight="fill" className="inline ml-1 text-[#00A850]" title="Approved" />}</td>
                  <td className="px-3 py-1.5 text-gray-800 max-w-[380px] truncate" title={r.name}>{r.name}</td>
                  <td className="px-3 py-1.5 text-right">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={r.offer}
                      onChange={(e) => setField(idx, "offer", e.target.value)}
                      onBlur={() => persistOffer(idx)}
                      readOnly={r.approved}
                      title={r.approved ? "Approved (locked) — unlock it on the Deals table to change" : "Edits are saved to the deal as an adjusted offer (feeds Total Offer)"}
                      className={`h-7 w-24 text-right bg-[#FFFFFF] border rounded-sm px-1 font-mono text-xs font-bold ${r.approved ? "border-[#00A850]/50 text-[#00A850] opacity-80 cursor-not-allowed" : "border-[#E4E4E0] text-[#00A850]"}`}
                      data-testid={`offer-price-${r.set_number}`}
                    />
                  </td>
                  {includeBl && (
                    <td className="px-3 py-1.5 text-right text-[#0055BF]">
                      {r.bl != null ? `$${Number(r.bl).toFixed(2)}` : "--"}
                      {r.bl_manual && <span className="text-[#E3000B] text-[9px] ml-1">M</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button onClick={copyClipboard} variant="outline"
            className="h-9 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#E3000B] hover:text-[#E3000B]"
            data-testid="offer-copy">
            COPY FOR SHEETS
          </Button>
          <Button onClick={exportCsv}
            className="h-9 rounded-sm bg-[#00A850] text-white font-bold uppercase tracking-wider hover:bg-[#00C060]"
            data-testid="offer-export">
            <Upload size={12} className="mr-1" /> EXPORT CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Inline approve: lock the reviewed price (approved > manual > SMART OFFER > auto). Click again to unlock. */
